import React, { useEffect, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import { last, sortBy } from 'lodash';
import moment from 'moment-timezone';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationUploadPlfFile } from 'api/mutations/uploadPlfFile.mutation';
import { useQueryGetLanguageList } from 'api/queries/getLanguageList.query';
import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { TreeCrumbs } from 'components/common/TreeCrumbs/TreeCrumbs';
import { getFileNameWithoutExtension } from 'components/common/UploadFileModal/components/UploadXerModal/UploadXerModal.utils';
import PlfFileListTable from 'components/common/UploadFileModal/components/UploadXerModal/component/PlfFileListTable/plfFileListTable';
import XerFilesListTable from 'components/common/UploadFileModal/components/UploadXerModal/component/XerFilesListTable/xerFilesListTable';
import { Details, FileExtensions, PlfItem } from 'components/common/UploadFileModal/components/programLibrary.types';
import classes from 'components/common/UploadFileModal/programLibraryUploadFiles.module.scss';
import { CustomizedFormErrorMessage, CustomizedTooltip } from 'components/common/index';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

const selectLastSubProgram = ({ projectHierarchyList, storedProject }) => {
    const p = projectHierarchyList?.children.find((c) => c.id === storedProject?.id);
    if (p) {
        const contract: any = last(p.children);
        if (contract) {
            return {
                id: contract.id,
                title: contract.name,
                uuid: contract.uuid,
            };
        }
    }
};

interface Props {
    details: Details;
    isUploading: boolean;
    handleFetchData: any;
    onClose: () => void;
    open: boolean;
}

export const UploadXerModal = ({ details: detailsFromFile, isUploading, handleFetchData, onClose, open }: Props) => {
    const { project: storedProject, contract: storedContract } = useProjectTreeStore();
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const { data: projectHierarchyList, isLoading: loadingGeneralList } =
        useQueryGetProgramGeneralHierarchyList('ProgramLibraryUploadFiles');
    const { mutateAsync } = useMutationUploadPlfFile();
    const { handleSnackBar } = useCustomSnackBar();

    const { data: languageList } = useQueryGetLanguageList();

    const mappedLanguageList = useMemo<ISelectOption<string>[]>(() => {
        return languageList ? sortBy(languageList, 'label') : [];
    }, [languageList]);

    const onSelectionFinish = ({ project, contract }) => {
        setSelectedProject(project);
        setSelectedContract(contract);
    };

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<Details>({
        values: {
            ...detailsFromFile,
            isIntegrated: detailsFromFile.versions.length > 1,
            customVersionName: getFileNameWithoutExtension(detailsFromFile.file.name),
            isBaseline: false,
            runCPM: false,
            language: null,
        },
    });

    const { fields } = useFieldArray({
        control,
        name: 'versions',
    });
    const details = watch();
    const disabledXer = details && details.versions.every((f) => !f.download);
    const list = details.versions
        .filter((item) => item.download)
        .map((item) => moment(item.versionDate).format('YYYY-MM-DD'));

    const dateError = !list.every((item) => item === list[0]);
    const disabled = disabledXer || isUploading;

    const handleFinishUpload = () => {
        if (!selectedContract) {
            handleSnackBar('Sub-Program is mandatory', 'error');
        } else {
            const payload = {
                details,
                contract: selectedContract,
                project: selectedProject,
            };
            handleFetchData(payload);
        }
    };

    const handlePlfUpload = async (e) => {
        if (e.target?.files) {
            const plfFile: File = e.target.files[0];
            const plfList: PlfItem[] = await mutateAsync({
                file: plfFile,
            });

            setValue('plf', plfFile);
            setValue('plfList', plfList);
            e.target.value = null;
        }
    };

    const selectedNode = useMemo(() => {
        return storedContract || selectLastSubProgram({ projectHierarchyList, storedProject });
    }, [storedProject, storedContract, projectHierarchyList]);

    useEffect(() => {
        selectedNode && setSelectedContract(selectedNode);
    }, [selectedNode]);

    useEffect(() => {
        storedProject && setSelectedProject(storedProject);
        storedContract && setSelectedContract(storedContract);
    }, [storedProject, storedContract]);

    const handleIsBaseline = (e: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
        setValue('isBaseline', e.target.checked);
        fields.forEach((item, index) => {
            setValue(`versions.${index}.isBaseline`, value, { shouldDirty: true });
        });
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            showCloseButton={!isUploading}
            actions={
                <>
                    <Button size={'medium'} variant={'secondary'} disabled={isUploading} onClick={onClose}>
                        Cancel
                    </Button>

                    {!details.plfList && !isUploading && details.type === FileExtensions.xer && (
                        <Button size={'medium'} variant={'secondary'} component={'label'}>
                            <span>Upload with Primavera Layout File</span>
                            <input
                                hidden
                                type={'file'}
                                id={'file'}
                                accept={'.plf'}
                                onChange={(e) => handlePlfUpload(e)}
                            />
                        </Button>
                    )}

                    <Button
                        type={'submit'}
                        size={'medium'}
                        variant={'primary'}
                        disabled={disabled}
                        isLoading={isUploading}
                        onClick={handleSubmit(handleFinishUpload)}
                    >
                        Upload
                    </Button>
                </>
            }
            title={'Upload XER File'}
        >
            <form>
                <div className={classes.ProgramLibraryUploadFilesContainer}>
                    <div className={classes.uploadFilesHeader}>
                        <div className={classes.uploadFilesHeaderSection}>
                            {loadingGeneralList ? (
                                <div>Loading</div>
                            ) : (
                                selectedContract?.uuid && (
                                    <TreeCrumbs
                                        selectedNode={selectedContract.uuid}
                                        onSelectionFinish={onSelectionFinish}
                                        data={projectHierarchyList}
                                        showAddNewButton={false}
                                        showAll={false}
                                    />
                                )
                            )}
                        </div>
                        <div className={classes.uploadFilesHeaderSection}>
                            <span>Source File:</span>
                            <span className={classes.uploadFilesHeaderName}>
                                <CustomizedTooltip
                                    tooltipPosition={TooltipPosition.Center}
                                    triggerElement={<div className={classes.fileName}>{details.file?.name}</div>}
                                    tooltipContent={details.file?.name}
                                />
                            </span>
                        </div>
                    </div>

                    <div className={classes.uploadFilesHeaderSentences}>
                        <span>{`Includes ${details.versions.length} versions.`}</span>
                        <span>Select which versions to upload</span>
                    </div>

                    <XerFilesListTable fields={fields} control={control} list={details.versions} setValue={setValue} />
                    {details.plfList && <PlfFileListTable plf={details.plfList} />}

                    <div className={classes.versionAdditionalProperties}>
                        <div>
                            <Controller
                                render={({ field }) => (
                                    <Switch
                                        {...field}
                                        checked={field.value}
                                        onChange={handleIsBaseline}
                                        label={'Is Baseline / Re-baseline'}
                                        labelPlacement={'start'}
                                    />
                                )}
                                name={'isBaseline'}
                                control={control}
                            ></Controller>
                        </div>

                        <div className={classes.languagesSelect}>
                            <Controller
                                render={({ field }) => (
                                    <Select<ISelectOption<string | null>>
                                        {...field}
                                        options={mappedLanguageList}
                                        isClearable={true}
                                        placeholder={'No Change'}
                                        label={'Language'}
                                    />
                                )}
                                name={`language`}
                                control={control}
                            />
                        </div>

                        {dateError && (
                            <Alert
                                severity={'warning'}
                                sx={{ fontSize: '1.6rem', borderRadius: 2, marginBottom: 2, marginTop: 2 }}
                            >
                                Note you have selected multiple programs to upload as an integrated program, but the
                                selected programs have mismatched data dates.
                            </Alert>
                        )}
                        {details.isIntegrated && (
                            <Alert
                                severity={'info'}
                                sx={{ fontSize: '1.6rem', borderRadius: 2, marginBottom: 2, marginTop: 2 }}
                            >
                                You have selected multiple programs to upload. They will be combined into a single
                                integrated program.
                            </Alert>
                        )}
                        <div>
                            <div className={classes.customVersionName}>
                                <Controller
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            placeholder={''}
                                            onClear={() => {
                                                setValue('customVersionName', '');
                                            }}
                                            label={'Integrated Program Name'}
                                        />
                                    )}
                                    name={`customVersionName`}
                                    control={control}
                                    rules={{
                                        required: 'Integrated Program Name is required',
                                        minLength: {
                                            value: 2,
                                            message: 'Integrated Program Name is too short',
                                        },
                                    }}
                                />
                            </div>
                        </div>
                        {errors.customVersionName && (
                            <div>
                                <CustomizedFormErrorMessage text={errors.customVersionName.message} />
                            </div>
                        )}
                        <div>
                            <Controller
                                render={({ field }) => (
                                    <Switch
                                        {...field}
                                        checked={field.value}
                                        label={'Apply CPM on integrated program'}
                                        labelPlacement={'start'}
                                    />
                                )}
                                name={'runCPM'}
                                control={control}
                            ></Controller>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    );
};
